import React, { useState, useEffect } from 'react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { Textarea } from '@/components/ui/textarea'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { Package, Plus, X, Target, Globe, Languages, Building, Users, DollarSign, TrendingUp, ShoppingCart, Trash2, ChevronDown, ChevronUp } from 'lucide-react'
import { toast } from 'sonner'
import type { ClientProductForm, Product, Territory } from '@/types/client'

interface ClientProductStepProps {
  data: ClientProductForm
  onChange: (data: Partial<ClientProductForm>) => void
  isLoading?: boolean
}

// Predefined options
const ECOM_PLATFORMS = [
  // Major Platforms
  "Shopify", "WooCommerce", "Magento", "BigCommerce", "Squarespace", "Wix",
  // Enterprise Platforms
  "Salesforce Commerce Cloud", "Adobe Commerce", "Oracle Commerce", "IBM WebSphere Commerce",
  // Open Source Platforms
  "PrestaShop", "OpenCart", "osCommerce", "Zen Cart", "Magento Open Source",
  // Headless/API-First
  "Shopify Plus", "Commerce.js", "Snipcart", "Medusa", "Saleor", "Sylius",
  // Regional Platforms
  "TecDoc", "OXID eSales", "Shopware", "Spryker", "Pimcore",
  // Niche Platforms
  "Volusion", "3dcart", "Ecwid", "Weebly", "GoDaddy Online Store",
  // Marketplace Platforms
  "Amazon Seller Central", "eBay", "Etsy", "Facebook Shop", "Instagram Shopping",
  // B2B Platforms
  "B2B Commerce", "Unilog", "Episerver Commerce", "Kentico", "Sitecore Commerce",
  // Mobile-First
  "Tapcart", "Shopgate", "Mobify", "PWA Store",
  // Custom/Other
  "Custom Solution", "Legacy System", "In-House Platform", "Other"
]
const TECH_STACK = [
  // Email Marketing & E-commerce Marketing (Prioritized)
  "Klaviyo", "Mailchimp", "Omnisend", "Attentive", "Constant Contact", "Campaign Monitor", "SendGrid", "AWeber",
  // Customer Support/Helpdesks (Prioritized)
  "Zendesk", "Intercom", "Freshdesk", "Help Scout", "Gorgias", "LiveChat", "Drift",
  // E-commerce Tools (Prioritized)
  "Yotpo", "Okendo", "Loox", "Judge.me", "Stamped.io", "Shopify Apps", "WooCommerce Extensions", "Magento Extensions", "BigCommerce Apps",
  // CRM Systems
  "HubSpot", "Salesforce", "Pipedrive", "Zoho CRM", "Monday.com", "Airtable",
  // Analytics & Tracking
  "Google Analytics", "Mixpanel", "Amplitude", "Segment", "Hotjar", "FullStory",
  // Marketing Automation
  "Marketo", "Pardot", "ActiveCampaign", "Drip", "ConvertKit", "GetResponse",
  // Integration Platforms
  "Zapier", "Make (Integromat)", "Microsoft Power Automate", "IFTTT", "Pabbly Connect",
  // Social Media & Ads
  "Facebook Pixel", "Google Ads", "LinkedIn Ads", "Twitter Ads", "TikTok Ads", "Snapchat Ads",
  // Payment Processing
  "Stripe", "PayPal", "Square", "Authorize.Net", "Braintree", "Adyen",
  // Inventory Management
  "TradeGecko", "Cin7", "inFlow", "Zoho Inventory", "Lightspeed",
  // Custom/Other
  "Custom Integration", "Legacy System", "In-House Solution", "Other"
]
const GEO_REGIONS = ['North America', 'Europe', 'APAC', 'Latin America', 'Middle East', 'Africa', 'Global']
const COUNTRIES = [
  // North America
  "United States", "Canada", "Mexico",
  // Europe
  "United Kingdom", "Germany", "France", "Spain", "Italy", "Netherlands", "Belgium", "Switzerland", "Austria", "Sweden", "Norway", "Denmark", "Finland", "Poland", "Czech Republic", "Hungary", "Portugal", "Ireland", "Greece", "Romania", "Bulgaria", "Croatia", "Slovenia", "Slovakia", "Estonia", "Latvia", "Lithuania",
  // Asia Pacific
  "Australia", "New Zealand", "Japan", "South Korea", "Singapore", "Hong Kong", "Taiwan", "Thailand", "Malaysia", "Indonesia", "Philippines", "Vietnam", "India", "China", "Pakistan", "Bangladesh", "Sri Lanka",
  // Middle East const COUNTRIES = ['United States', 'Canada', 'United Kingdom', 'Germany', 'France', 'Spain', 'Italy', 'Netherlands', 'Australia', 'Japan', 'China', 'India', 'Brazil', 'Mexico'] Africa
  "United Arab Emirates", "Saudi Arabia", "Israel", "Turkey", "South Africa", "Nigeria", "Kenya", "Egypt", "Morocco", "Tunisia",
  // Latin America
  "Brazil", "Argentina", "Chile", "Colombia", "Peru", "Uruguay", "Paraguay", "Bolivia", "Ecuador", "Venezuela",
  // Other
  "Russia", "Ukraine", "Belarus", "Kazakhstan", "Other"
]
const INDUSTRIES = ['E-commerce', 'SaaS', 'Retail', 'Manufacturing', 'Healthcare', 'Finance', 'Education', 'Technology', 'Other']

// Helper functions for number formatting with commas
const formatNumberWithCommas = (value: number | undefined | string | ''): string => {
  if (value === undefined || value === '' || value === null) return ''
  const numValue = typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) : value
  if (isNaN(numValue)) return ''
  return numValue.toLocaleString('en-US')
}

const parseNumberFromFormatted = (value: string): number | undefined => {
  if (!value) return undefined
  const cleaned = value.replace(/,/g, '')
  const parsed = parseFloat(cleaned)
  return isNaN(parsed) ? undefined : parsed
}

export function ClientProductStep({ data, onChange, isLoading }: ClientProductStepProps) {
  const [products, setProducts] = useState<Product[]>(() => {
    return data.products || []
  })

  const [territories, setTerritories] = useState<Territory[]>(() => {
    return data.territories || []
  })

  // Expand/collapse state for products
  const [expandedProducts, setExpandedProducts] = useState<Set<string>>(() => {
    // Initialize with only the last product expanded
    return products.length > 0 
      ? new Set([products[products.length - 1].id])
      : new Set()
  })

  // Expand/collapse state for territories
  const [expandedTerritories, setExpandedTerritories] = useState<Set<string>>(() => {
    // Initialize with only the last territory expanded
    return territories.length > 0 
      ? new Set([territories[territories.length - 1].id])
      : new Set()
  })

  // Sync state with prop changes (in case data is updated from outside)
  useEffect(() => {
    if (data.products) {
      setProducts(data.products)
      // Ensure at least one product is expanded if products exist
      setExpandedProducts(prev => {
        if (data.products.length > 0 && prev.size === 0) {
          return new Set([data.products[data.products.length - 1].id])
        }
        // Filter out any expanded IDs that no longer exist
        const existingIds = new Set(data.products.map(p => p.id))
        const filtered = new Set(Array.from(prev).filter(id => existingIds.has(id)))
        return filtered.size > 0 ? filtered : (data.products.length > 0 ? new Set([data.products[data.products.length - 1].id]) : new Set())
      })
    }
    if (data.territories) {
      setTerritories(data.territories)
      // Ensure at least one territory is expanded if territories exist
      setExpandedTerritories(prev => {
        if (data.territories.length > 0 && prev.size === 0) {
          return new Set([data.territories[data.territories.length - 1].id])
        }
        // Filter out any expanded IDs that no longer exist
        const existingIds = new Set(data.territories.map(t => t.id))
        const filtered = new Set(Array.from(prev).filter(id => existingIds.has(id)))
        return filtered.size > 0 ? filtered : (data.territories.length > 0 ? new Set([data.territories[data.territories.length - 1].id]) : new Set())
      })
    }
  }, [data.products?.length, data.territories?.length])
  
  // Custom field states
  const [customEcomPlatform, setCustomEcomPlatform] = useState("")
  const [customTechStack, setCustomTechStack] = useState("")
  const [customCountry, setCustomCountry] = useState("")
  const [customIndustry, setCustomIndustry] = useState("")
  const [showCustomPlatformDialog, setShowCustomPlatformDialog] = useState<string | null>(null) // 'included' | 'excluded' | null
  const [showCustomTechDialog, setShowCustomTechDialog] = useState<string | null>(null) // 'required' | 'excluded' | null

  const updateProducts = (updatedProducts: Product[]) => {
    setProducts(updatedProducts)
    onChange({
      products: updatedProducts,
      // Legacy compatibility
      product_names: updatedProducts.map(p => p.name),
      product_categories: updatedProducts.length === 1 ? updatedProducts[0].category : '',
      product_description: updatedProducts.length === 1 ? updatedProducts[0].description : '',
    })
  }

  const updateTerritories = (updatedTerritories: Territory[]) => {
    setTerritories(updatedTerritories)
    onChange({
      territories: updatedTerritories,
    })
  }

  const addProduct = () => {
    const newProduct: Product = {
      id: `product-${Date.now()}`,
      name: '',
      category: '',
      description: '',
      ecom_platforms: { included: [], excluded: [] },
      tech_stack: { required: [], excluded: [] },
      business_metrics: {},
      geo_targeting: { regions: [], countries_included: [], countries_excluded: [] },
      company_size: {
        smb: { definition: 'Companies with 1-50 employees, <$10M revenue', criteria: { max_employees: 50, max_revenue: 10000000 } },
        mid_market: { definition: 'Companies with 51-500 employees, $10M-$100M revenue', criteria: { min_employees: 51, max_employees: 500, min_revenue: 10000000, max_revenue: 100000000 } },
        enterprise: { definition: 'Companies with 500+ employees, $100M+ revenue', criteria: { min_employees: 500, min_revenue: 100000000 } }
      },
      // Legacy fields
      languages: [],
      geos: [],
      company_sizes: [],
      industries: [],
      icp_requirements: {
        titles: [],
        departments: [],
        company_size: [],
        industries: [],
        technologies: []
      }
    }
    updateProducts([...products, newProduct])
    // Collapse all other products, expand only the new one
    setExpandedProducts(new Set([newProduct.id]))
    toast.success('New product added!')
  }

  const updateProduct = (productId: string, updates: Partial<Product>) => {
    const updatedProducts = products.map(product =>
      product.id === productId ? { ...product, ...updates } : product
    )
    updateProducts(updatedProducts)
  }

  const removeProduct = (productId: string) => {
    const updatedProducts = products.filter(product => product.id !== productId)
    updateProducts(updatedProducts)
    // If removed product was expanded, expand the last remaining product
    if (expandedProducts.has(productId) && updatedProducts.length > 0) {
      setExpandedProducts(new Set([updatedProducts[updatedProducts.length - 1].id]))
    } else {
      setExpandedProducts(new Set(expandedProducts))
    }
    toast.success('Product removed!')
  }

  const addTerritory = () => {
    const newTerritory: Territory = {
      id: `territory-${Date.now()}`,
      name: '',
      geo_targeting: { regions: [], countries_included: [], countries_excluded: [] },
      languages: [],
      company_size_focus: {
        smb: { kpi: '', min: 0, max: 0 },
        mid_market: { kpi: '', min: 0, max: 0 },
        enterprise: { kpi: '', min: 0, max: 0 }
      },
      // Legacy fields
      geo_regions: [],
      industry_focus: [],
      icp_requirements: {
        titles: [],
        departments: [],
        company_size: [],
        industries: [],
        technologies: []
      }
    }
    updateTerritories([...territories, newTerritory])
    // Collapse all other territories, expand only the new one
    setExpandedTerritories(new Set([newTerritory.id]))
    toast.success('New territory added!')
  }

  const updateTerritory = (territoryId: string, updates: Partial<Territory>) => {
    const updatedTerritories = territories.map(territory =>
      territory.id === territoryId ? { ...territory, ...updates } : territory
    )
    updateTerritories(updatedTerritories)
  }

  const removeTerritory = (territoryId: string) => {
    const updatedTerritories = territories.filter(territory => territory.id !== territoryId)
    updateTerritories(updatedTerritories)
    // If removed territory was expanded, expand the last remaining territory
    if (expandedTerritories.has(territoryId) && updatedTerritories.length > 0) {
      setExpandedTerritories(new Set([updatedTerritories[updatedTerritories.length - 1].id]))
    } else {
      setExpandedTerritories(new Set(expandedTerritories))
    }
    toast.success('Territory removed!')
  }

  return (
    <div className="space-y-6">
      {/* Section 1: Products */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Package className="h-5 w-5 text-primary" />
              <CardTitle>Products</CardTitle>
            </div>
            <Button onClick={addProduct} disabled={isLoading} size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Add Product
            </Button>
          </div>
          <CardDescription>
            Define what products/services your business sells with detailed targeting parameters
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {products.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No products defined yet. Click "Add Product" to get started.</p>
            </div>
          ) : (
            products.map((product, index) => (
              <Card key={product.id} className="border-l-4 border-l-primary">
<CardHeader className="pb-3">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <Badge variant="secondary" className="shrink-0 whitespace-nowrap">Product {index + 1}</Badge>
                      <Input
                        value={product.name}
                        onChange={(e) => updateProduct(product.id, { name: e.target.value })}
                        placeholder="Product name (e.g., Reviews Platform)"
                        className="font-semibold flex-1 min-w-0"
                        disabled={isLoading}
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const newExpanded = new Set(expandedProducts)
                          if (expandedProducts.has(product.id)) {
                            newExpanded.delete(product.id)
                          } else {
                            newExpanded.add(product.id)
                          }
                          setExpandedProducts(newExpanded)
                        }}
                        disabled={isLoading}
                        className="shrink-0"
                      >
                        {expandedProducts.has(product.id) ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                      </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeProduct(product.id)}
                      disabled={isLoading}
                      className="shrink-0"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                    </div>
                  </div>
                </CardHeader>
                {expandedProducts.has(product.id) && (
                <CardContent className="space-y-6">
                  {/* Basic Information */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      <Label className="shrink-0 whitespace-nowrap w-20">Category</Label>
                      <Input
                        value={product.category || ''}
                        onChange={(e) => updateProduct(product.id, { category: e.target.value })}
                        placeholder="e.g., SaaS, E-commerce Tools"
                        className="flex-1"
                        disabled={isLoading}
                      />
                    </div>
                    <div className="flex items-start gap-2">
                      <Label className="shrink-0 whitespace-nowrap w-20 mt-2">Description</Label>
                      <Textarea
                        value={product.description || ''}
                        onChange={(e) => updateProduct(product.id, { description: e.target.value })}
                        placeholder="What does this product do?"
                        className="flex-1 min-h-[2.5rem] resize-y"
                        rows={2}
                        disabled={isLoading}
                      />
                    </div>
                  </div>

                  {/* E-commerce Platforms */}
                  <div className="space-y-3">
                    <Label className="text-sm font-medium flex items-center space-x-1">
                      <ShoppingCart className="h-4 w-4" />
                      <span>E-commerce Platforms</span>
                    </Label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                        <Label className="text-xs text-muted-foreground">Included Platforms</Label>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setShowCustomPlatformDialog(`${product.id}-included`)}
                            disabled={isLoading}
                            className="h-6 px-2"
                          >
                            <Plus className="h-3 w-3 mr-1" />
                            Add Platform
                          </Button>
                        </div>
                        <SearchableSelect
                          options={ECOM_PLATFORMS.filter(platform => 
                            !product.ecom_platforms.included.includes(platform)
                          )}
                          onSelect={(value) => {
                            if (!product.ecom_platforms.included.includes(value)) {
                              updateProduct(product.id, { 
                                ecom_platforms: { 
                                  ...product.ecom_platforms, 
                                  included: [...product.ecom_platforms.included, value] 
                                } 
                              })
                            }
                          }}
                          placeholder="Add platform"
                          disabled={isLoading}
                          emptyMessage="No platforms found."
                          className="w-full"
                        />
                        <div className="flex flex-wrap gap-1">
                          {product.ecom_platforms.included.map(platform => (
                            <Badge key={platform} variant="secondary" className="text-xs">
                              {platform}
                              <button
                                onClick={() => updateProduct(product.id, { 
                                  ecom_platforms: { 
                                    ...product.ecom_platforms, 
                                    included: product.ecom_platforms.included.filter(p => p !== platform) 
                                  } 
                                })}
                                className="ml-1 hover:text-destructive"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </Badge>
                          ))}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                        <Label className="text-xs text-muted-foreground">Excluded Platforms</Label>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setShowCustomPlatformDialog(`${product.id}-excluded`)}
                            disabled={isLoading}
                            className="h-6 px-2"
                          >
                            <Plus className="h-3 w-3 mr-1" />
                            Add Platform
                          </Button>
                        </div>
                        <SearchableSelect
                          options={ECOM_PLATFORMS.filter(platform => 
                            !product.ecom_platforms.excluded.includes(platform)
                          )}
                          onSelect={(value) => {
                            if (!product.ecom_platforms.excluded.includes(value)) {
                              updateProduct(product.id, { 
                                ecom_platforms: { 
                                  ...product.ecom_platforms, 
                                  excluded: [...product.ecom_platforms.excluded, value] 
                                } 
                              })
                            }
                          }}
                          placeholder="Add platform"
                          disabled={isLoading}
                          emptyMessage="No platforms found."
                          className="w-full"
                        />
                        <div className="flex flex-wrap gap-1">
                          {product.ecom_platforms.excluded.map(platform => (
                            <Badge key={platform} variant="destructive" className="text-xs">
                              {platform}
                              <button
                                onClick={() => updateProduct(product.id, { 
                                  ecom_platforms: { 
                                    ...product.ecom_platforms, 
                                    excluded: product.ecom_platforms.excluded.filter(p => p !== platform) 
                                  } 
                                })}
                                className="ml-1 hover:text-destructive"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Tech Stack Requirements */}
                  <div className="space-y-3">
                    <Label className="text-sm font-medium flex items-center space-x-1">
                      <Building className="h-4 w-4" />
                      <span>Tech Stack Requirements</span>
                    </Label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                        <Label className="text-xs text-muted-foreground">Required Technologies</Label>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setShowCustomTechDialog(`${product.id}-required`)}
                            disabled={isLoading}
                            className="h-6 px-2"
                          >
                            <Plus className="h-3 w-3 mr-1" />
                            Add Technology
                          </Button>
                        </div>
                        <SearchableSelect
                          options={TECH_STACK.filter(tech => 
                            !product.tech_stack.required.includes(tech)
                          )}
                          onSelect={(value) => {
                            if (!product.tech_stack.required.includes(value)) {
                              updateProduct(product.id, { 
                                tech_stack: { 
                                  ...product.tech_stack, 
                                  required: [...product.tech_stack.required, value] 
                                } 
                              })
                            }
                          }}
                          placeholder="Add technology"
                          disabled={isLoading}
                          emptyMessage="No technologies found."
                          className="w-full"
                        />
                        <div className="flex flex-wrap gap-1">
                          {product.tech_stack.required.map(tech => (
                            <Badge key={tech} variant="secondary" className="text-xs">
                              {tech}
                              <button
                                onClick={() => updateProduct(product.id, { 
                                  tech_stack: { 
                                    ...product.tech_stack, 
                                    required: product.tech_stack.required.filter(t => t !== tech) 
                                  } 
                                })}
                                className="ml-1 hover:text-destructive"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </Badge>
                          ))}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                        <Label className="text-xs text-muted-foreground">Excluded Technologies</Label>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setShowCustomTechDialog(`${product.id}-excluded`)}
                            disabled={isLoading}
                            className="h-6 px-2"
                          >
                            <Plus className="h-3 w-3 mr-1" />
                            Add Technology
                          </Button>
                        </div>
                        <SearchableSelect
                          options={TECH_STACK.filter(tech => 
                            !product.tech_stack.excluded.includes(tech)
                          )}
                          onSelect={(value) => {
                            if (!product.tech_stack.excluded.includes(value)) {
                              updateProduct(product.id, { 
                                tech_stack: { 
                                  ...product.tech_stack, 
                                  excluded: [...product.tech_stack.excluded, value] 
                                } 
                              })
                            }
                          }}
                          placeholder="Add technology"
                          disabled={isLoading}
                          emptyMessage="No technologies found."
                          className="w-full"
                        />
                        <div className="flex flex-wrap gap-1">
                          {product.tech_stack.excluded.map(tech => (
                            <Badge key={tech} variant="destructive" className="text-xs">
                              {tech}
                              <button
                                onClick={() => updateProduct(product.id, { 
                                  tech_stack: { 
                                    ...product.tech_stack, 
                                    excluded: product.tech_stack.excluded.filter(t => t !== tech) 
                                  } 
                                })}
                                className="ml-1 hover:text-destructive"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Business Metrics */}
                  <div className="space-y-3">
                    <Label className="text-sm font-medium flex items-center space-x-1">
                      <TrendingUp className="h-4 w-4" />
                      <span>Business Metrics & KPIs</span>
                    </Label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">Revenue Range ($)</Label>
                        <div className="flex space-x-2">
                          <Input
                            type="text"
                            placeholder="Min"
                            value={formatNumberWithCommas(product.business_metrics?.revenue_range?.min)}
                            onChange={(e) => {
                              const parsed = parseNumberFromFormatted(e.target.value)
                              updateProduct(product.id, { 
                                business_metrics: { 
                                  ...product.business_metrics, 
                                  revenue_range: { 
                                    ...product.business_metrics?.revenue_range, 
                                    min: parsed !== undefined ? parsed : 0 
                                  } 
                                } 
                              })
                            }}
                            disabled={isLoading}
                          />
                          <Input
                            type="text"
                            placeholder="Max"
                            value={formatNumberWithCommas(product.business_metrics?.revenue_range?.max)}
                            onChange={(e) => {
                              const parsed = parseNumberFromFormatted(e.target.value)
                              updateProduct(product.id, { 
                                business_metrics: { 
                                  ...product.business_metrics, 
                                  revenue_range: { 
                                    ...product.business_metrics?.revenue_range, 
                                    max: parsed !== undefined ? parsed : 0 
                                  } 
                                } 
                              })
                            }}
                            disabled={isLoading}
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">Employee Count</Label>
                        <div className="flex space-x-2">
                          <Input
                            type="text"
                            placeholder="Min"
                            value={formatNumberWithCommas(product.business_metrics?.employee_count?.min)}
                            onChange={(e) => {
                              const parsed = parseNumberFromFormatted(e.target.value)
                              updateProduct(product.id, { 
                                business_metrics: { 
                                  ...product.business_metrics, 
                                  employee_count: { 
                                    ...product.business_metrics?.employee_count, 
                                    min: parsed !== undefined ? parsed : 0 
                                  } 
                                } 
                              })
                            }}
                            disabled={isLoading}
                          />
                          <Input
                            type="text"
                            placeholder="Max"
                            value={formatNumberWithCommas(product.business_metrics?.employee_count?.max)}
                            onChange={(e) => {
                              const parsed = parseNumberFromFormatted(e.target.value)
                              updateProduct(product.id, { 
                                business_metrics: { 
                                  ...product.business_metrics, 
                                  employee_count: { 
                                    ...product.business_metrics?.employee_count, 
                                    max: parsed !== undefined ? parsed : 0 
                                  } 
                                } 
                              })
                            }}
                            disabled={isLoading}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Custom KPIs */}
                    <div className="space-y-3 mt-4 pt-4 border-t">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm font-medium">Custom KPIs</Label>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const newKPI = {
                              id: `kpi-${Date.now()}`,
                              name: '',
                              min: undefined,
                              max: undefined
                            }
                            const currentCustomMetrics = product.business_metrics?.custom_metrics || []
                            updateProduct(product.id, {
                              business_metrics: {
                                ...product.business_metrics,
                                custom_metrics: [...currentCustomMetrics, newKPI]
                              }
                            })
                          }}
                          disabled={isLoading}
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Add Custom KPI
                        </Button>
                      </div>

                      {product.business_metrics?.custom_metrics && product.business_metrics.custom_metrics.length > 0 && (
                        <div className="space-y-3">
                          {product.business_metrics.custom_metrics.map((kpi, index) => (
                            <div key={kpi.id || index} className="p-3 border rounded-lg space-y-2">
                              <div className="flex items-center justify-between gap-2">
                                <Input
                                  type="text"
                                  placeholder="KPI Name (e.g., Monthly Orders, Minimum Spend)"
                                  value={kpi.name || ''}
                                  onChange={(e) => {
                                    const updatedMetrics = [...(product.business_metrics?.custom_metrics || [])]
                                    updatedMetrics[index] = { ...updatedMetrics[index], name: e.target.value }
                                    updateProduct(product.id, {
                                      business_metrics: {
                                        ...product.business_metrics,
                                        custom_metrics: updatedMetrics
                                      }
                                    })
                                  }}
                                  disabled={isLoading}
                                  className="flex-1"
                                />
                                {index > 0 && (
                                  <Select
                                    value={kpi.logic || 'AND'}
                                    onValueChange={(value) => {
                                      const updatedMetrics = [...(product.business_metrics?.custom_metrics || [])]
                                      updatedMetrics[index] = { ...updatedMetrics[index], logic: value as 'AND' | 'OR' }
                                      updateProduct(product.id, {
                                        business_metrics: {
                                          ...product.business_metrics,
                                          custom_metrics: updatedMetrics
                                        }
                                      })
                                    }}
                                  >
                                    <SelectTrigger className="w-20">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="AND">AND</SelectItem>
                                      <SelectItem value="OR">OR</SelectItem>
                                    </SelectContent>
                                  </Select>
                                )}
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    const updatedMetrics = product.business_metrics?.custom_metrics?.filter((_, i) => i !== index) || []
                                    updateProduct(product.id, {
                                      business_metrics: {
                                        ...product.business_metrics,
                                        custom_metrics: updatedMetrics
                                      }
                                    })
                                  }}
                                  disabled={isLoading}
                                  className="ml-2"
                                >
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                              </div>
                              <div className="flex space-x-2">
                                <div className="flex-1">
                                  <Label className="text-xs text-muted-foreground">Min</Label>
                                  <Input
                                    type="text"
                                    placeholder="Minimum"
                                    value={formatNumberWithCommas(kpi.min)}
                                    onChange={(e) => {
                                      const parsed = parseNumberFromFormatted(e.target.value)
                                      const updatedMetrics = [...(product.business_metrics?.custom_metrics || [])]
                                      updatedMetrics[index] = {
                                        ...updatedMetrics[index],
                                        min: parsed
                                      }
                                      updateProduct(product.id, {
                                        business_metrics: {
                                          ...product.business_metrics,
                                          custom_metrics: updatedMetrics
                                        }
                                      })
                                    }}
                                    disabled={isLoading}
                                  />
                                </div>
                                <div className="flex-1">
                                  <Label className="text-xs text-muted-foreground">Max</Label>
                                  <Input
                                    type="text"
                                    placeholder="Maximum"
                                    value={formatNumberWithCommas(kpi.max)}
                                    onChange={(e) => {
                                      const parsed = parseNumberFromFormatted(e.target.value)
                                      const updatedMetrics = [...(product.business_metrics?.custom_metrics || [])]
                                      updatedMetrics[index] = {
                                        ...updatedMetrics[index],
                                        max: parsed
                                      }
                                      updateProduct(product.id, {
                                        business_metrics: {
                                          ...product.business_metrics,
                                          custom_metrics: updatedMetrics
                                        }
                                      })
                                    }}
                                    disabled={isLoading}
                                  />
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {(!product.business_metrics?.custom_metrics || product.business_metrics.custom_metrics.length === 0) && (
                        <p className="text-sm text-muted-foreground italic">
                          No custom KPIs added. Click "Add Custom KPI" to create one.
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Geographic Targeting */}
                  <div className="space-y-3">
                    <Label className="text-sm font-medium flex items-center space-x-1">
                      <Globe className="h-4 w-4" />
                      <span>Geographic Targeting</span>
                    </Label>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">Regions</Label>
                        <SearchableSelect
                          options={GEO_REGIONS.filter(region => !product.geo_targeting.regions.includes(region))}
                          onSelect={(value) => {
                            if (!product.geo_targeting.regions.includes(value)) {
                              updateProduct(product.id, { 
                                geo_targeting: { 
                                  ...product.geo_targeting, 
                                  regions: [...product.geo_targeting.regions, value] 
                                } 
                              })
                            }
                          }}
                          placeholder="Add region"
                          disabled={isLoading}
                          emptyMessage="No regions found."
                          className="h-10"
                        />
                        <div className="flex flex-wrap gap-1">
                          {product.geo_targeting.regions.map(region => (
                            <Badge key={region} variant="secondary" className="text-xs">
                              {region}
                              <button
                                onClick={() => updateProduct(product.id, { 
                                  geo_targeting: { 
                                    ...product.geo_targeting, 
                                    regions: product.geo_targeting.regions.filter(r => r !== region) 
                                  } 
                                })}
                                className="ml-1 hover:text-destructive"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </Badge>
                          ))}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">Countries Included</Label>
                        <SearchableSelect
                          options={COUNTRIES.filter(country => !product.geo_targeting.countries_included.includes(country))}
                          onSelect={(value) => {
                            if (!product.geo_targeting.countries_included.includes(value)) {
                              updateProduct(product.id, { 
                                geo_targeting: { 
                                  ...product.geo_targeting, 
                                  countries_included: [...product.geo_targeting.countries_included, value] 
                                } 
                              })
                            }
                          }}
                          placeholder="Add country"
                          disabled={isLoading}
                          emptyMessage="No countries found."
                          className="h-10"
                        />
                        <div className="flex flex-wrap gap-1">
                          {product.geo_targeting.countries_included.map(country => (
                            <Badge key={country} variant="secondary" className="text-xs">
                              {country}
                              <button
                                onClick={() => updateProduct(product.id, { 
                                  geo_targeting: { 
                                    ...product.geo_targeting, 
                                    countries_included: product.geo_targeting.countries_included.filter(c => c !== country) 
                                  } 
                                })}
                                className="ml-1 hover:text-destructive"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </Badge>
                          ))}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">Countries Excluded</Label>
                        <SearchableSelect
                          options={COUNTRIES.filter(country => !product.geo_targeting.countries_excluded.includes(country))}
                          onSelect={(value) => {
                            if (!product.geo_targeting.countries_excluded.includes(value)) {
                              updateProduct(product.id, { 
                                geo_targeting: { 
                                  ...product.geo_targeting, 
                                  countries_excluded: [...product.geo_targeting.countries_excluded, value] 
                                } 
                              })
                            }
                          }}
                          placeholder="Add country"
                          disabled={isLoading}
                          emptyMessage="No countries found."
                          className="h-10"
                        />
                        <div className="flex flex-wrap gap-1">
                          {product.geo_targeting.countries_excluded.map(country => (
                            <Badge key={country} variant="destructive" className="text-xs">
                              {country}
                              <button
                                onClick={() => updateProduct(product.id, { 
                                  geo_targeting: { 
                                    ...product.geo_targeting, 
                                    countries_excluded: product.geo_targeting.countries_excluded.filter(c => c !== country) 
                                  } 
                                })}
                                className="ml-1 hover:text-destructive"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
                )}
              </Card>
            ))
          )}
        </CardContent>
      </Card>

      {/* Section 2: Territories/Segments */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Target className="h-5 w-5 text-primary" />
              <CardTitle>Territories & Segments</CardTitle>
            </div>
            <Button onClick={addTerritory} disabled={isLoading} size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Add Territory
            </Button>
          </div>
          <CardDescription>
            Define your target market segments and territories with clear company size definitions
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {territories.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Target className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No territories defined yet. Click "Add Territory" to get started.</p>
            </div>
          ) : (
            territories.map((territory, index) => (
              <Card key={territory.id} className="border-l-4 border-l-blue-500">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <Badge variant="secondary" className="shrink-0 whitespace-nowrap">Territory {index + 1}</Badge>
                      <Input
                        value={territory.name}
                        onChange={(e) => updateTerritory(territory.id, { name: e.target.value })}
                        placeholder="Territory name (e.g., Enterprise Global, UK SMB)"
                        className="font-semibold flex-1 min-w-0"
                        disabled={isLoading}
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const newExpanded = new Set(expandedTerritories)
                          if (expandedTerritories.has(territory.id)) {
                            newExpanded.delete(territory.id)
                          } else {
                            newExpanded.add(territory.id)
                          }
                          setExpandedTerritories(newExpanded)
                        }}
                        disabled={isLoading}
                        className="shrink-0"
                      >
                        {expandedTerritories.has(territory.id) ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                      </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeTerritory(territory.id)}
                      disabled={isLoading}
                      className="shrink-0"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                    </div>
                  </div>
                </CardHeader>
                {expandedTerritories.has(territory.id) && (
                <CardContent className="space-y-6">
                  {/* Geographic Targeting */}
                  <div className="space-y-3">
                    <Label className="text-sm font-medium flex items-center space-x-1">
                      <Globe className="h-4 w-4" />
                      <span>Geographic Targeting</span>
                    </Label>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">Regions</Label>
                        <SearchableSelect
                          options={GEO_REGIONS.filter(region => !territory.geo_targeting.regions.includes(region))}
                          onSelect={(value) => {
                            if (!territory.geo_targeting.regions.includes(value)) {
                              updateTerritory(territory.id, { 
                                geo_targeting: { 
                                  ...territory.geo_targeting, 
                                  regions: [...territory.geo_targeting.regions, value] 
                                } 
                              })
                            }
                          }}
                          placeholder="Add region"
                          disabled={isLoading}
                          emptyMessage="No regions found."
                          className="h-10"
                        />
                        <div className="flex flex-wrap gap-1">
                          {territory.geo_targeting.regions.map(region => (
                            <Badge key={region} variant="secondary" className="text-xs">
                              {region}
                              <button
                                onClick={() => updateTerritory(territory.id, { 
                                  geo_targeting: { 
                                    ...territory.geo_targeting, 
                                    regions: territory.geo_targeting.regions.filter(r => r !== region) 
                                  } 
                                })}
                                className="ml-1 hover:text-destructive"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </Badge>
                          ))}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">Countries Included</Label>
                        <SearchableSelect
                          options={COUNTRIES.filter(country => !territory.geo_targeting.countries_included.includes(country))}
                          onSelect={(value) => {
                            if (!territory.geo_targeting.countries_included.includes(value)) {
                              updateTerritory(territory.id, { 
                                geo_targeting: { 
                                  ...territory.geo_targeting, 
                                  countries_included: [...territory.geo_targeting.countries_included, value] 
                                } 
                              })
                            }
                          }}
                          placeholder="Add country"
                          disabled={isLoading}
                          emptyMessage="No countries found."
                          className="h-10"
                        />
                        <div className="flex flex-wrap gap-1">
                          {territory.geo_targeting.countries_included.map(country => (
                            <Badge key={country} variant="secondary" className="text-xs">
                              {country}
                              <button
                                onClick={() => updateTerritory(territory.id, { 
                                  geo_targeting: { 
                                    ...territory.geo_targeting, 
                                    countries_included: territory.geo_targeting.countries_included.filter(c => c !== country) 
                                  } 
                                })}
                                className="ml-1 hover:text-destructive"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </Badge>
                          ))}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">Countries Excluded</Label>
                        <SearchableSelect
                          options={COUNTRIES.filter(country => !territory.geo_targeting.countries_excluded.includes(country))}
                          onSelect={(value) => {
                            if (!territory.geo_targeting.countries_excluded.includes(value)) {
                              updateTerritory(territory.id, { 
                                geo_targeting: { 
                                  ...territory.geo_targeting, 
                                  countries_excluded: [...territory.geo_targeting.countries_excluded, value] 
                                } 
                              })
                            }
                          }}
                          placeholder="Add country"
                          disabled={isLoading}
                          emptyMessage="No countries found."
                          className="h-10"
                        />
                        <div className="flex flex-wrap gap-1">
                          {territory.geo_targeting.countries_excluded.map(country => (
                            <Badge key={country} variant="destructive" className="text-xs">
                              {country}
                              <button
                                onClick={() => updateTerritory(territory.id, { 
                                  geo_targeting: { 
                                    ...territory.geo_targeting, 
                                    countries_excluded: territory.geo_targeting.countries_excluded.filter(c => c !== country) 
                                  } 
                                })}
                                className="ml-1 hover:text-destructive"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Language Requirements */}
                  <div className="space-y-3">
                    <Label className="text-sm font-medium flex items-center space-x-1">
                      <Languages className="h-4 w-4" />
                      <span>Language Requirements</span>
                    </Label>
                    <Select
                      value=""
                      onValueChange={(value) => {
                        if (!territory.languages.includes(value)) {
                          updateTerritory(territory.id, { languages: [...territory.languages, value] })
                        }
                      }}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Add language" />
                      </SelectTrigger>
                      <SelectContent>
                        {['English', 'French', 'German', 'Spanish', 'Italian', 'Portuguese', 'Dutch', 'Swedish', 'Norwegian', 'Danish'].map(lang => (
                          <SelectItem key={lang} value={lang}>{lang}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <div className="flex flex-wrap gap-1">
                      {territory.languages.map(lang => (
                        <Badge key={lang} variant="secondary" className="text-xs">
                          {lang}
                          <button
                            onClick={() => updateTerritory(territory.id, { 
                              languages: territory.languages.filter(l => l !== lang) 
                            })}
                            className="ml-1 hover:text-destructive"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  </div>

                  {/* Company Size Focus - Editable Segments */}
                  <div className="space-y-3">
                    <Label className="text-sm font-medium flex items-center space-x-1">
                      <Building className="h-4 w-4" />
                      <span>Company Size Focus</span>
                    </Label>
                    <div className="space-y-3">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {/* SMB Segment */}
                        <div className="space-y-2 p-3 border rounded bg-muted/20">
                          <Label className="text-xs font-medium text-muted-foreground">SMB Segment</Label>
                          <div className="space-y-2">
                            <Input
                              placeholder="KPI (e.g., Revenue, Employees)"
                              value={territory.company_size_focus?.smb?.kpi || ""}
                              onChange={(e) => updateTerritory(territory.id, { 
                                company_size_focus: { 
                                  ...territory.company_size_focus, 
                                  smb: { 
                                    ...territory.company_size_focus?.smb, 
                                    kpi: e.target.value 
                                  } 
                                } 
                              })}
                              className="h-8 text-xs"
                              disabled={isLoading}
                            />
                            <div className="flex space-x-1">
                              <Input
                                type="number"
                                placeholder="Min"
                                value={territory.company_size_focus?.smb?.min || ""}
                                onChange={(e) => updateTerritory(territory.id, { 
                                  company_size_focus: { 
                                    ...territory.company_size_focus, 
                                    smb: { 
                                      ...territory.company_size_focus?.smb, 
                                      min: parseInt(e.target.value) || 0 
                                    } 
                                  } 
                                })}
                                className="h-8 text-xs"
                                disabled={isLoading}
                              />
                              <Input
                                type="number"
                                placeholder="Max"
                                value={territory.company_size_focus?.smb?.max || ""}
                                onChange={(e) => updateTerritory(territory.id, { 
                                  company_size_focus: { 
                                    ...territory.company_size_focus, 
                                    smb: { 
                                      ...territory.company_size_focus?.smb, 
                                      max: parseInt(e.target.value) || 0 
                                    } 
                                  } 
                                })}
                                className="h-8 text-xs"
                                disabled={isLoading}
                              />
                            </div>
                          </div>
                        </div>
                        {/* Mid-Market Segment */}
                        <div className="space-y-2 p-3 border rounded bg-muted/20">
                          <Label className="text-xs font-medium text-muted-foreground">Mid-Market Segment</Label>
                          <div className="space-y-2">
                            <Input
                              placeholder="KPI (e.g., Revenue, Employees)"
                              value={territory.company_size_focus?.mid_market?.kpi || ""}
                              onChange={(e) => updateTerritory(territory.id, { 
                                company_size_focus: { 
                                  ...territory.company_size_focus, 
                                  mid_market: { 
                                    ...territory.company_size_focus?.mid_market, 
                                    kpi: e.target.value 
                                  } 
                                } 
                              })}
                              className="h-8 text-xs"
                              disabled={isLoading}
                            />
                            <div className="flex space-x-1">
                              <Input
                                type="number"
                                placeholder="Min"
                                value={territory.company_size_focus?.mid_market?.min || ""}
                                onChange={(e) => updateTerritory(territory.id, { 
                                  company_size_focus: { 
                                    ...territory.company_size_focus, 
                                    mid_market: { 
                                      ...territory.company_size_focus?.mid_market, 
                                      min: parseInt(e.target.value) || 0 
                                    } 
                                  } 
                                })}
                                className="h-8 text-xs"
                                disabled={isLoading}
                              />
                              <Input
                                type="number"
                                placeholder="Max"
                                value={territory.company_size_focus?.mid_market?.max || ""}
                                onChange={(e) => updateTerritory(territory.id, { 
                                  company_size_focus: { 
                                    ...territory.company_size_focus, 
                                    mid_market: { 
                                      ...territory.company_size_focus?.mid_market, 
                                      max: parseInt(e.target.value) || 0 
                                    } 
                                  } 
                                })}
                                className="h-8 text-xs"
                                disabled={isLoading}
                              />
                            </div>
                          </div>
                        </div>
                        {/* Enterprise Segment */}
                        <div className="space-y-2 p-3 border rounded bg-muted/20">
                          <Label className="text-xs font-medium text-muted-foreground">Enterprise Segment</Label>
                          <div className="space-y-2">
                            <Input
                              placeholder="KPI (e.g., Revenue, Employees)"
                              value={territory.company_size_focus?.enterprise?.kpi || ""}
                              onChange={(e) => updateTerritory(territory.id, { 
                                company_size_focus: { 
                                  ...territory.company_size_focus, 
                                  enterprise: { 
                                    ...territory.company_size_focus?.enterprise, 
                                    kpi: e.target.value 
                                  } 
                                } 
                              })}
                              className="h-8 text-xs"
                              disabled={isLoading}
                            />
                            <div className="flex space-x-1">
                              <Input
                                type="number"
                                placeholder="Min"
                                value={territory.company_size_focus?.enterprise?.min || ""}
                                onChange={(e) => updateTerritory(territory.id, { 
                                  company_size_focus: { 
                                    ...territory.company_size_focus, 
                                    enterprise: { 
                                      ...territory.company_size_focus?.enterprise, 
                                      min: parseInt(e.target.value) || 0 
                                    } 
                                  } 
                                })}
                                className="h-8 text-xs"
                                disabled={isLoading}
                              />
                              <Input
                                type="number"
                                placeholder="Max"
                                value={territory.company_size_focus?.enterprise?.max || ""}
                                onChange={(e) => updateTerritory(territory.id, { 
                                  company_size_focus: { 
                                    ...territory.company_size_focus, 
                                    enterprise: { 
                                      ...territory.company_size_focus?.enterprise, 
                                      max: parseInt(e.target.value) || 0 
                                    } 
                                  } 
                                })}
                                className="h-8 text-xs"
                                disabled={isLoading}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        <p><strong>Example:</strong> AU SMB - Region: AU, Territory: SMB, KPI: Revenue, Min: 1k, Max: 100k</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
                )}
              </Card>
            ))
          )}
        </CardContent>
      </Card>

      {/* Summary */}
      <Card className="border-green-200 bg-green-50">
        <CardContent className="p-4">
          <div className="flex items-center space-x-2 text-green-800">
            <Package className="h-4 w-4" />
            <div className="text-sm">
              <p className="font-medium">Setup Summary</p>
              <p>
                {products.length} products • {territories.length} territories • Ready for team assignments
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Custom Platform Dialog */}
      {showCustomPlatformDialog && (
        <Dialog open={true} onOpenChange={() => setShowCustomPlatformDialog(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Custom Platform</DialogTitle>
              <DialogDescription>Enter a custom platform name to add to the list</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Platform Name</Label>
                <Input
                  value={customEcomPlatform}
                  onChange={(e) => setCustomEcomPlatform(e.target.value)}
                  placeholder="e.g., CustomShop"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && customEcomPlatform.trim()) {
                      const [productId, type] = showCustomPlatformDialog.split('-')
                      const product = products.find(p => p.id === productId)
                      if (product && !ECOM_PLATFORMS.includes(customEcomPlatform.trim())) {
                        ECOM_PLATFORMS.push(customEcomPlatform.trim())
                      }
                      if (product) {
                        if (type === 'included' && !product.ecom_platforms.included.includes(customEcomPlatform.trim())) {
                          updateProduct(product.id, {
                            ecom_platforms: {
                              ...product.ecom_platforms,
                              included: [...product.ecom_platforms.included, customEcomPlatform.trim()]
                            }
                          })
                        } else if (type === 'excluded' && !product.ecom_platforms.excluded.includes(customEcomPlatform.trim())) {
                          updateProduct(product.id, {
                            ecom_platforms: {
                              ...product.ecom_platforms,
                              excluded: [...product.ecom_platforms.excluded, customEcomPlatform.trim()]
                            }
                          })
                        }
                      }
                      setCustomEcomPlatform('')
                      setShowCustomPlatformDialog(null)
                      toast.success('Custom platform added')
                    }
                  }}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => {
                  setShowCustomPlatformDialog(null)
                  setCustomEcomPlatform('')
                }}>
                  Cancel
                </Button>
                <Button onClick={() => {
                  if (!customEcomPlatform.trim()) {
                    toast.error('Please enter a platform name')
                    return
                  }
                  const [productId, type] = showCustomPlatformDialog.split('-')
                  const product = products.find(p => p.id === productId)
                  if (product && !ECOM_PLATFORMS.includes(customEcomPlatform.trim())) {
                    ECOM_PLATFORMS.push(customEcomPlatform.trim())
                  }
                  if (product) {
                    if (type === 'included' && !product.ecom_platforms.included.includes(customEcomPlatform.trim())) {
                      updateProduct(product.id, {
                        ecom_platforms: {
                          ...product.ecom_platforms,
                          included: [...product.ecom_platforms.included, customEcomPlatform.trim()]
                        }
                      })
                    } else if (type === 'excluded' && !product.ecom_platforms.excluded.includes(customEcomPlatform.trim())) {
                      updateProduct(product.id, {
                        ecom_platforms: {
                          ...product.ecom_platforms,
                          excluded: [...product.ecom_platforms.excluded, customEcomPlatform.trim()]
                        }
                      })
                    }
                  }
                  setCustomEcomPlatform('')
                  setShowCustomPlatformDialog(null)
                  toast.success('Custom platform added')
                }}>
                  Add Platform
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Custom Technology Dialog */}
      {showCustomTechDialog && (
        <Dialog open={true} onOpenChange={() => setShowCustomTechDialog(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Custom Technology</DialogTitle>
              <DialogDescription>Enter a custom technology name to add to the list</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Technology Name</Label>
                <Input
                  value={customTechStack}
                  onChange={(e) => setCustomTechStack(e.target.value)}
                  placeholder="e.g., CustomCRM"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && customTechStack.trim()) {
                      const [productId, type] = showCustomTechDialog.split('-')
                      const product = products.find(p => p.id === productId)
                      if (product && !TECH_STACK.includes(customTechStack.trim())) {
                        TECH_STACK.push(customTechStack.trim())
                      }
                      if (product) {
                        if (type === 'required' && !product.tech_stack.required.includes(customTechStack.trim())) {
                          updateProduct(product.id, {
                            tech_stack: {
                              ...product.tech_stack,
                              required: [...product.tech_stack.required, customTechStack.trim()]
                            }
                          })
                        } else if (type === 'excluded' && !product.tech_stack.excluded.includes(customTechStack.trim())) {
                          updateProduct(product.id, {
                            tech_stack: {
                              ...product.tech_stack,
                              excluded: [...product.tech_stack.excluded, customTechStack.trim()]
                            }
                          })
                        }
                      }
                      setCustomTechStack('')
                      setShowCustomTechDialog(null)
                      toast.success('Custom technology added')
                    }
                  }}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => {
                  setShowCustomTechDialog(null)
                  setCustomTechStack('')
                }}>
                  Cancel
                </Button>
                <Button onClick={() => {
                  if (!customTechStack.trim()) {
                    toast.error('Please enter a technology name')
                    return
                  }
                  const [productId, type] = showCustomTechDialog.split('-')
                  const product = products.find(p => p.id === productId)
                  if (product && !TECH_STACK.includes(customTechStack.trim())) {
                    TECH_STACK.push(customTechStack.trim())
                  }
                  if (product) {
                    if (type === 'required' && !product.tech_stack.required.includes(customTechStack.trim())) {
                      updateProduct(product.id, {
                        tech_stack: {
                          ...product.tech_stack,
                          required: [...product.tech_stack.required, customTechStack.trim()]
                        }
                      })
                    } else if (type === 'excluded' && !product.tech_stack.excluded.includes(customTechStack.trim())) {
                      updateProduct(product.id, {
                        tech_stack: {
                          ...product.tech_stack,
                          excluded: [...product.tech_stack.excluded, customTechStack.trim()]
                        }
                      })
                    }
                  }
                  setCustomTechStack('')
                  setShowCustomTechDialog(null)
                  toast.success('Custom technology added')
                }}>
                  Add Technology
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}
